import React, { useEffect, useState } from 'react';
import { Styles } from './MUIStyles';
import Header from '@components/Header';
import Navbar from '@components/Navbar';
import AppBanner from '@components/Banner/appBanner';
import Footer from '@components/Footer/Footer';
import { useMediaQuery, useTheme } from '@mui/material';
import { useOrderRequestStore } from '@stores/OrderRequest/OrderRequest';
import { IOrderRequestPayload, Operacao } from '@stores/OrderRequest/types';
import { useLocalidadesIbgeStore } from '@stores/LocalidadesIbge/LocalidadesIbge';
import { maskPhone, isValidPhone, isValidEmail } from '@utils/masks';
import FeedbackModal from '@components/FeedbackModal';
import Loading from '@components/Loading/Loading';
import SEO from '@components/SEO';
import { useLocation } from 'react-router-dom';

type FormData = {
  operacoes: Operacao[];
  tipos: string[];
  desiredCity: string;
  details: string;
  name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  neighborhood: string;
};

const OrderFormScreen: React.FC = () => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const submitOrder = useOrderRequestStore((s) => s.submitOrder);
  const {
    municipios,
    loading: loadingCidades,
    fetchDistritosByUF
  } = useLocalidadesIbgeStore();
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [modal, setModal] = useState<{
    open: boolean;
    variant: 'success' | 'error';
    title: string;
    message: string;
  }>({
    open: false,
    variant: 'success',
    title: '',
    message: ''
  });

  const [formData, setFormData] = useState<FormData>({
    operacoes: [],
    tipos: [],
    desiredCity: '',
    details: '',
    name: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    neighborhood: ''
  });

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    if (name === 'state') {
      fetchDistritosByUF(value);
      setFormData((prev) => ({ ...prev, state: value, city: '' }));
      return;
    }
    if (name === 'phone') {
      const masked = maskPhone(value);
      setFormData((prev) => ({ ...prev, phone: masked }));
      if (phoneError)
        setPhoneError(!masked || isValidPhone(masked) ? '' : phoneError);
      return;
    }
    if (name === 'email') {
      setFormData((prev) => ({ ...prev, email: value }));
      if (emailError) setEmailError(isValidEmail(value) ? '' : emailError);
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneBlur = () => {
    if (formData.phone && !isValidPhone(formData.phone)) {
      setPhoneError('Telefone inválido. Use (XX) XXXX-XXXX ou (XX) XXXXX-XXXX');
    } else {
      setPhoneError('');
    }
  };

  const handleEmailBlur = () => {
    if (formData.email && !isValidEmail(formData.email)) {
      setEmailError('E-mail inválido');
    } else {
      setEmailError('');
    }
  };

  const toggleOperacao = (value: Operacao) => {
    setFormData((prev) => {
      const exists = prev.operacoes.includes(value);
      return {
        ...prev,
        operacoes: exists
          ? prev.operacoes.filter((v) => v !== value)
          : [...prev.operacoes, value]
      };
    });
  };

  const toggleTipo = (value: string) => {
    setFormData((prev) => {
      const exists = prev.tipos.includes(value);
      return {
        ...prev,
        tipos: exists
          ? prev.tipos.filter((v) => v !== value)
          : [...prev.tipos, value]
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const pErr =
      formData.phone && !isValidPhone(formData.phone)
        ? 'Telefone inválido. Use (XX) XXXX-XXXX ou (XX) XXXXX-XXXX'
        : '';
    const eErr = !isValidEmail(formData.email) ? 'E-mail inválido' : '';
    setPhoneError(pErr);
    setEmailError(eErr);
    if (pErr || eErr) return;

    const payload: IOrderRequestPayload = {
      operacoes: formData.operacoes,
      tipos: formData.tipos,
      cidade_desejada: formData.desiredCity || undefined,
      detalhes: formData.details || undefined,
      nome: formData.name,
      email: formData.email,
      telefone: formData.phone || undefined,
      uf: formData.state || undefined,
      cidade: formData.city || undefined,
      bairro: formData.neighborhood || undefined
    };

    setSubmitting(true);
    try {
      const res = await submitOrder(payload);
      if (res) {
        setFormData({
          operacoes: [],
          tipos: [],
          desiredCity: '',
          details: '',
          name: '',
          email: '',
          phone: '',
          city: '',
          state: '',
          neighborhood: ''
        });
        setModal({
          open: true,
          variant: 'success',
          title: 'Encomenda recebida!',
          message:
            'Suas preferências foram registradas. Entraremos em contato assim que encontrarmos um imóvel ideal para você!'
        });
      } else {
        setModal({
          open: true,
          variant: 'error',
          title: 'Falha no envio',
          message:
            'Não foi possível registrar sua encomenda. Verifique os dados e tente novamente.'
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const operacaoOptions: Operacao[] = ['compra', 'aluguel', 'temporada'];

  const propertyTypeOptions = [
    'Área',
    'Casa',
    'Fazenda',
    'Galpão',
    'Indústria',
    'Sítio',
    'Chácara',
    'Flat',
    'Loja',
    'Sala',
    'Terreno',
    'Apartamento'
  ];

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0 });
    }, 100);
  }, [location]);

  return (
    <>
      <SEO
        title="Encomende seu Imóvel"
        description="Encomende o imóvel dos seus sonhos. Informe suas preferências e nossa equipe encontrará as melhores opções de compra ou aluguel para você em Sorocaba e região."
      />
      {submitting && <Loading />}
      <FeedbackModal
        open={modal.open}
        variant={modal.variant}
        title={modal.title}
        message={modal.message}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
      />
      <Header />
      <Navbar />
      <Styles.Main>
        <Styles.LabelTipo>
          Preencha o formulário abaixo com as características do imóvel que
          deseja, pois entraremos em contato com você assim que encontrarmos um
          imóvel com o perfil informado.
        </Styles.LabelTipo>
        <Styles.Container>
          <form onSubmit={handleSubmit}>
            <div
              style={{
                display: 'flex',
                gap: '40px',
                marginBottom: '-15px',
                justifyContent: 'space-between',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
              <div style={{ width: isMobile ? '100%' : 'auto' }}>
                <Styles.SectionTitle>O que deseja?</Styles.SectionTitle>
                <Styles.FormRow
                  style={{
                    flexGrow: 1,
                    width: '100%',
                    flexWrap: 'wrap',
                    gap: '15px'
                  }}>
                  {operacaoOptions.map((op) => (
                    <Styles.CheckboxLabel
                      key={op}
                      style={{ width: isMobile ? '100%' : 'auto' }}>
                      <input
                        type="checkbox"
                        name="operacoes"
                        value={op}
                        checked={formData.operacoes.includes(op)}
                        onChange={() => toggleOperacao(op)}
                      />
                      {op.charAt(0).toUpperCase() + op.slice(1)}
                    </Styles.CheckboxLabel>
                  ))}
                </Styles.FormRow>

                <Styles.InputContainer style={{ width: '100%' }}>
                  <Styles.SectionTitle>Cidade Desejada</Styles.SectionTitle>
                  <Styles.Input
                    type="text"
                    name="desiredCity"
                    value={formData.desiredCity}
                    onChange={handleInputChange}
                    style={{
                      height: '40px',
                      width: '100%',
                      padding: '8px 12px'
                    }}
                  />
                </Styles.InputContainer>
              </div>

              <div style={{ width: '50%', marginRight: 20 }}>
                <Styles.SectionTitle>
                  Está procurando qual tipo de imóvel?
                </Styles.SectionTitle>
                <Styles.FormRow
                  style={{
                    flexGrow: 1,
                    width: '100%',
                    flexWrap: 'wrap',
                    gap: '15px'
                  }}>
                  {propertyTypeOptions.map((type) => (
                    <Styles.CheckboxLabel key={type}>
                      <input
                        type="checkbox"
                        name="tipos"
                        value={type}
                        checked={formData.tipos.includes(type)}
                        onChange={() => toggleTipo(type)}
                      />
                      {type}
                    </Styles.CheckboxLabel>
                  ))}
                </Styles.FormRow>
              </div>
            </div>

            <Styles.FormRow
              style={{
                flexDirection: isMobile ? 'column' : 'row',
                gap: '20px',
                paddingBottom: '40px',
                marginTop: 40
              }}>
              <Styles.InputContainer
                style={{
                  width: isMobile ? '100%' : '60%',
                  height: isMobile ? '200px' : '150px'
                }}>
                <Styles.SectionTitle>
                  Informe mais detalhes...
                </Styles.SectionTitle>
                <Styles.TextArea
                  name="details"
                  value={formData.details}
                  onChange={handleInputChange}
                  style={{ height: isMobile ? '150px' : '100px' }}
                />
              </Styles.InputContainer>
            </Styles.FormRow>

            <Styles.SectionTitle>Seus dados</Styles.SectionTitle>
            <Styles.FormRow>
              <Styles.InputContainer>
                <Styles.Label className="required">Nome</Styles.Label>
                <Styles.Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </Styles.InputContainer>

              <Styles.InputContainer width="25%">
                <Styles.Label className="required">E-mail</Styles.Label>
                <Styles.Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleEmailBlur}
                  required
                />
                {emailError && (
                  <small
                    style={{
                      color: '#d32f2f',
                      fontSize: '12px',
                      marginTop: '2px'
                    }}>
                    {emailError}
                  </small>
                )}
              </Styles.InputContainer>
              <Styles.InputContainer width="15%">
                <Styles.Label className="required">Telefone</Styles.Label>
                <Styles.Input
                  type="text"
                  name="phone"
                  placeholder="(XX) XXXXX-XXXX"
                  value={formData.phone}
                  onChange={handleInputChange}
                  onBlur={handlePhoneBlur}
                />
                {phoneError && (
                  <small
                    style={{
                      color: '#d32f2f',
                      fontSize: '12px',
                      marginTop: '2px'
                    }}>
                    {phoneError}
                  </small>
                )}
              </Styles.InputContainer>
            </Styles.FormRow>

            <Styles.FormRow
              style={{
                flexDirection: isMobile ? 'column' : 'row',
                gap: '20px'
              }}>
              <Styles.InputContainer width="10%">
                <Styles.Label>UF</Styles.Label>
                <Styles.Select
                  as="select"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}>
                  <option value="">Selecione</option>
                  <option value="AC">AC</option>
                  <option value="AL">AL</option>
                  <option value="AP">AP</option>
                  <option value="AM">AM</option>
                  <option value="BA">BA</option>
                  <option value="CE">CE</option>
                  <option value="DF">DF</option>
                  <option value="ES">ES</option>
                  <option value="GO">GO</option>
                  <option value="MA">MA</option>
                  <option value="MT">MT</option>
                  <option value="MS">MS</option>
                  <option value="MG">MG</option>
                  <option value="PA">PA</option>
                  <option value="PB">PB</option>
                  <option value="PR">PR</option>
                  <option value="PE">PE</option>
                  <option value="PI">PI</option>
                  <option value="RJ">RJ</option>
                  <option value="RN">RN</option>
                  <option value="RS">RS</option>
                  <option value="RO">RO</option>
                  <option value="RR">RR</option>
                  <option value="SC">SC</option>
                  <option value="SP">SP</option>
                  <option value="SE">SE</option>
                  <option value="TO">TO</option>
                </Styles.Select>
              </Styles.InputContainer>

              <Styles.InputContainer width="25%">
                <Styles.Label>Cidade</Styles.Label>
                <Styles.Select
                  as="select"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  disabled={!formData.state || loadingCidades}>
                  <option value="">
                    {loadingCidades ? 'Carregando...' : 'Selecione'}
                  </option>
                  {municipios.map((m) => (
                    <option key={m.id} value={m.nome}>
                      {m.nome}
                    </option>
                  ))}
                </Styles.Select>
              </Styles.InputContainer>

              <Styles.InputContainer width="25.7%">
                <Styles.Label>Bairro</Styles.Label>
                <Styles.Input
                  type="text"
                  name="neighborhood"
                  value={formData.neighborhood}
                  onChange={handleInputChange}
                />
              </Styles.InputContainer>
            </Styles.FormRow>

            <Styles.LabelTipo style={{ margin: '20px 0', textAlign: 'center' }}>
              * Campos obrigatórios
            </Styles.LabelTipo>
            <Styles.Button
              type="submit"
              style={{
                width: isMobile ? '100%' : '150px',
                marginLeft: isMobile ? '0' : '5px'
              }}>
              ENVIAR
            </Styles.Button>
          </form>
        </Styles.Container>
      </Styles.Main>
      {!isMobile && <AppBanner />}
      <Footer />
    </>
  );
};

export default OrderFormScreen;
